import React, { useState, useContext, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";

//pages & includes
import ManageSidebar from "../ManageSidebar";

//functions
import { _t, getCookie } from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import IngredientStockPrint from "./IngredientStockPrint";
import { useReactToPrint } from "react-to-print";
import DataTable from "../../../../common/DataTable";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const StockReceiving = () => {
  const { t } = useTranslation();

  let { loading, setLoading } = useContext(SettingsContext);

  // local list state
  const [receivingList, setReceivingList] = useState(null);
  const [receivingForSearch, setReceivingForSearch] = useState(null);
  const [dataPaginating, setDataPaginating] = useState(false);

  // search state
  const [searchResult, setSearchResult] = useState({
    list: null,
    searched: false,
  });

  // fetch paginated list
  const fetchReceivingList = async (page = 1) => {
    setDataPaginating(true);
    try {
      const res = await axios.get(
        BASE_URL + `/settings/stock-recieving?page=${page}`,
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
      setReceivingList(res.data[0]);
      setReceivingForSearch(res.data[1]);
    } catch (error) {
      console.log("error==>", error);
      ToastError({ title: _t(t("Please try again")) });
    } finally {
      setDataPaginating(false);
    }
  };

  useEffect(() => {
    fetchReceivingList(1);
  }, []);

  const handleSearch = (e) => {
    const searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0 || receivingForSearch === null) {
      setSearchResult({ ...searchResult, searched: false });
    } else {
      const searchedList = receivingForSearch.filter((item) =>
        item.document_identity.toLowerCase().includes(searchInput) ||
        item.document_date.toLowerCase().includes(searchInput) ||
        (item.remarks && item.remarks.toLowerCase().includes(searchInput)) ||
        (item.total_amount && item.total_amount.toLowerCase().includes(searchInput))
    
      );
      setSearchResult({ ...searchResult, list: searchedList, searched: true });
    }
  };

  // print states and functions
  const [newRow, setNewRow] = useState([]);
  const [postingId, setPostingId] = useState(null);
  const [printData, setPrintData] = useState({
    document_identity: "",
    documentDate: "",
    ref_id: "",
    fbranch: "",
    tbranch: "",
    remarks: "",
  });
  const componentRef = useRef();

  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page { margin: 15px 25px; }`,
  });

  const handleGetPrintData = async (id) => {
    
    setPrintData({});
    setNewRow([]);

    const url = BASE_URL + "/settings/stock-recieving/" + parseInt(id);
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setPrintData({
          ...printData,
          documentDate: new Date(res.data.document_date),
          document_identity: res.data.document_identity,
          issuance_doc_identity: res.data.issuance_doc_identity,
          requisition_doc_identity: res.data.requisition_doc_identity,
          fbranch: res.data.from_branch,
          tbranch: res.data.to_branch,
          remarks: res.data.remarks,
          created_at: res.data.created_at,
        });
        if (res.data.stock_acceptance_detail?.length > 0) {
          setNewRow([...res.data.stock_acceptance_detail]);
        }
        handleOnlyPrint();
      }
    } catch (error) {
      console.log("error==>", error);
      ToastError({ title: _t(t("Please try again")) });
    }
  };

  const handlePostApi = async (id) => {
    setLoading(true);
    setPostingId(id);
    try {
      const res = await axios.post(
        BASE_URL + "/settings/stock-recieving-post",
        { id, is_post: 1 },
        { headers: { Authorization: `Bearer ${getCookie()}` } }
      );
      setReceivingList(res.data[0]);
      setReceivingForSearch(res.data[1]);
      setSearchResult({ ...searchResult, list: res.data[1] });
      ToastSuccess({ title: _t(t("Stock receiving has been posted")) });
    } catch (err) {
      const message = err.response?.data?.message;
      ToastError({ title: _t(t(message || "Please try again")) });
    } finally {
      setLoading(false);
      setPostingId(null);
    }
  };

  const COLUMNS = [
    { label: "S/L", render: (item, i) => i + 1 },
    { label: "Document Number", key: "document_identity" },
    { label: "Document Date", key: "document_date" },
    { label: "From Branch", render: (item) => item.from_branch?.name },
    { label: "To Branch", render: (item) => item.to_branch?.name },
    { label: "Issue Ref", render: (item) => item.issuance_doc_identity },
    { label: "Req Ref", render: (item) => item.requisition_doc_identity },
    {
      label: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center">
          <button
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handleGetPrintData(item.id)}
          >
            <i className="fa fa-print edit-icon"></i>
          </button>
          {item?.is_post === 0 ? (
            <>
              <button
                className="btn btn-dark ml-2 rounded-sm del-btn"
                title="Post receiving"
                style={{ padding: "1px 0px 0px 1px" }}
                onClick={() => handlePostApi(item.id)}
                disabled={postingId === item.id}
              >
                <i className="fa fa-check-square-o" aria-hidden="true"></i>
              </button>
              <NavLink
                className="btn btn-primary ml-2 rounded-sm"
                to={`/dashboard/manage/stock/stock-receiving-edit/${item.id}`}
                style={{ height: "1.7rem", width: "1.7rem" }}
              >
                <div className="d-flex align-items-center justify-content-center">
                  <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
                </div>
              </NavLink>
            </>
          ) : (
            <NavLink
              className="btn btn-warning ml-2 rounded-sm"
              to={`/dashboard/manage/stock/stock-receiving-view/${item.id}`}
              style={{ height: "1.7rem", width: "1.7rem" }}
            >
              <div className="d-flex align-items-center justify-content-center">
                <i className="fa fa-eye edit-icon" aria-hidden="true"></i>
              </div>
            </NavLink>
          )}
        </div>
      ),
    },
    { label: "Created By", render: (item) => item.created_by_name },
    { label: "Updated By", render: (item) => item.updated_by_name },
    { label: "Posted By", render: (item) => item.posted_by_name },
  ];

  const getListData = () => {
    if (searchResult.searched) {
      return searchResult.list || [];
    }
    return receivingList?.data || [];
  };

  const handleFetchData = (page) => {
    fetchReceivingList(page);
  };

  const onClearSearch = () => {
    setSearchResult({ list: null, searched: false });
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Stock Receiving"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div key="fragment3">
                <div className="t-bg-white">
                  <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                      <ul className="t-list fk-breadcrumb">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {_t(t("Stock Receiving List"))}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6 col-lg-7">
                      <div className="row gx-3 align-items-center">
                        <div className="col-md-9 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search")) + ".."}
                                onChange={handleSearch}
                                disabled={loading || dataPaginating}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                            >
                              <i className="fa fa-search" aria-hidden="true"></i>
                            </button>
                          </div>
                        </div>
                        <div className="col-md-3 text-md-right">
                          <NavLink
                            to="/dashboard/manage/stock/stock-receiving-add"
                            className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                          >
                            {_t(t("add new"))}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  loading={loading || dataPaginating}
                  data={getListData()}
                  columns={COLUMNS}
                  emptyText="No data available"
                  pagination={{
                    isPageLoading: dataPaginating,
                    isPagination: true,
                    pageCount: receivingList?.last_page,
                    currentPage: receivingList?.current_page,
                    from: receivingList?.from,
                    to: receivingList?.to,
                    total: receivingList?.total,
                  }}
                  handleFetchData={handleFetchData}
                  searchPayload={{
                    isSearched: searchResult.searched,
                    onClearSearch,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </main>


      <IngredientStockPrint
        name="Stock Receiving"
        newConsumption={printData}
        newRow={newRow}
        print={componentRef}
      />
    </>
  );
};

export default StockReceiving;
